import { getProto } from '../../im/pb-imports';
import { appendBuffer, buildPdu, tryReadOnePdu, type ParsedPdu } from '../../im/pdu';
import { getWebDeviceId } from '../../shared/utils/device';

const SID_FILE = 5;

function recordFileWsDebug(event: Record<string, unknown>) {
  try {
    const target = window as Window & {
      __locsFileWsDebug?: Record<string, unknown>[];
    };
    target.__locsFileWsDebug ??= [];
    target.__locsFileWsDebug.push({
      ts: Date.now(),
      ...event,
    });
    if (target.__locsFileWsDebug.length > 200) {
      target.__locsFileWsDebug.splice(0, target.__locsFileWsDebug.length - 200);
    }
    const doc = document;
    let sink = doc.getElementById('locs-file-ws-debug');
    if (!sink) {
      sink = doc.createElement('pre');
      sink.id = 'locs-file-ws-debug';
      sink.setAttribute(
        'style',
        'position:fixed;left:-99999px;top:0;max-width:1px;max-height:1px;overflow:hidden;opacity:0;pointer-events:none;z-index:-1;',
      );
      doc.body.prepend(sink);
    }
    sink.textContent = target.__locsFileWsDebug
      .slice(-12)
      .map((item) => JSON.stringify(item))
      .join('\n');
  } catch {
    // Ignore debug sink failures.
  }
}

export type FileWsClientHandlers = {
  onState?: (state: unknown) => void;
  onPullDataReq?: (req: unknown) => void;
  onPullDataRsp?: (rsp: unknown) => void;
  onClose?: () => void;
};

export class FileWsClient {
  private ws: WebSocket | null = null;
  private rx = new Uint8Array(0);
  private loginPromise: Promise<unknown> | null = null;
  private resolveLogin: ((value: unknown) => void) | null = null;
  private rejectLogin: ((reason?: unknown) => void) | null = null;
  private heartbeatTimer: number | null = null;

  constructor(
    private readonly wsUrl: string,
    private readonly handlers: FileWsClientHandlers = {},
  ) {}

  connectAndLogin(userId: number, taskId: string, fileRole: number, authTicket = '', deviceId = getWebDeviceId()): Promise<unknown> {
    if (this.loginPromise) {
      return this.loginPromise;
    }
    const proto = getProto();
    console.info('[FILE_WS] connecting file server gateway', {
      url: this.wsUrl,
      taskId,
      fileRole,
    });
    recordFileWsDebug({ phase: 'connectStart', taskId, fileRole, url: this.wsUrl });
    this.loginPromise = new Promise((resolve, reject) => {
      this.resolveLogin = resolve;
      this.rejectLogin = reject;

      const ws = new WebSocket(this.wsUrl);
      ws.binaryType = 'arraybuffer';
      this.ws = ws;

      ws.onopen = () => {
        recordFileWsDebug({ phase: 'open', taskId, fileRole, url: this.wsUrl });
        const req = new proto.IM.File.IMFileLoginReq();
        req.setUserId(userId);
        req.setTaskId(taskId);
        req.setFileRole(fileRole);
        req.setAuthTicket(authTicket);
        req.setDeviceId(deviceId);
        ws.send(buildPdu(
          SID_FILE,
          proto.IM.BaseDefine.FileCmdID.CID_FILE_LOGIN_REQ,
          req.serializeBinary(),
        ));
        this.startHeartbeat();
      };

      ws.onmessage = (event) => {
        const chunk = new Uint8Array(event.data as ArrayBuffer);
        recordFileWsDebug({ phase: 'message', taskId, bytes: chunk.length });
        this.rx = appendBuffer(this.rx, chunk);
        this.drain();
      };

      ws.onerror = () => {
        recordFileWsDebug({ phase: 'error', taskId, url: this.wsUrl });
        reject(new Error('File websocket error'));
      };

      ws.onclose = () => {
        recordFileWsDebug({ phase: 'close', taskId, url: this.wsUrl });
        if (this.rejectLogin) {
          this.rejectLogin(new Error('File websocket closed'));
        }
        this.cleanupLoginState();
        this.handlers.onClose?.();
      };
    });
    return this.loginPromise;
  }

  requestChunk(taskId: string, userId: number, transMode: number, offset: number, dataSize: number) {
    recordFileWsDebug({ phase: 'requestChunk', taskId, userId, transMode, offset, dataSize });
    const proto = getProto();
    const req = new proto.IM.File.IMFilePullDataReq();
    req.setTaskId(taskId);
    req.setUserId(userId);
    req.setTransMode(transMode);
    req.setOffset(offset);
    req.setDataSize(dataSize);
    this.sendPdu(proto.IM.BaseDefine.FileCmdID.CID_FILE_PULL_DATA_REQ, req.serializeBinary());
  }

  sendChunk(taskId: string, userId: number, offset: number, bytes: Uint8Array) {
    recordFileWsDebug({ phase: 'sendChunk', taskId, userId, offset, bytes: bytes.length });
    const proto = getProto();
    const rsp = new proto.IM.File.IMFilePullDataRsp();
    rsp.setResultCode(0);
    rsp.setTaskId(taskId);
    rsp.setUserId(userId);
    rsp.setOffset(offset);
    rsp.setData(bytes);
    this.sendPdu(proto.IM.BaseDefine.FileCmdID.CID_FILE_PULL_DATA_RSP, rsp.serializeBinary());
  }

  sendState(taskId: string, userId: number, state: number) {
    recordFileWsDebug({ phase: 'sendState', taskId, userId, state });
    const proto = getProto();
    const req = new proto.IM.File.IMFileState();
    req.setTaskId(taskId);
    req.setUserId(userId);
    req.setState(state);
    this.sendPdu(proto.IM.BaseDefine.FileCmdID.CID_FILE_STATE, req.serializeBinary());
  }

  close() {
    this.stopHeartbeat();
    this.ws?.close();
    this.ws = null;
    this.rx = new Uint8Array(0);
    this.cleanupLoginState();
  }

  private cleanupLoginState() {
    this.loginPromise = null;
    this.resolveLogin = null;
    this.rejectLogin = null;
  }

  private startHeartbeat() {
    this.stopHeartbeat();
    this.heartbeatTimer = window.setInterval(() => {
      if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
        return;
      }
      const proto = getProto();
      const heartbeat = new proto.IM.Other.IMHeartBeat();
      this.ws.send(buildPdu(
        proto.IM.BaseDefine.ServiceID.SID_OTHER,
        proto.IM.BaseDefine.OtherCmdID.CID_OTHER_HEARTBEAT,
        heartbeat.serializeBinary(),
      ));
    }, 5000);
  }

  private stopHeartbeat() {
    if (this.heartbeatTimer !== null) {
      window.clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }

  private sendPdu(commandId: number, body: Uint8Array) {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      throw new Error('File websocket not open');
    }
    const pdu = buildPdu(SID_FILE, commandId, body);
    const headerLength =
      ((pdu[0] ?? 0) << 24) |
      ((pdu[1] ?? 0) << 16) |
      ((pdu[2] ?? 0) << 8) |
      (pdu[3] ?? 0);
    recordFileWsDebug({
      phase: 'sendPdu',
      commandId,
      bodyBytes: body.length,
      pduBytes: pdu.length,
      headerLength: headerLength >>> 0,
      readyState: this.ws.readyState,
      bufferedAmountBefore: this.ws.bufferedAmount,
    });
    this.ws.send(pdu);
    recordFileWsDebug({
      phase: 'sendPduDone',
      commandId,
      pduBytes: pdu.length,
      bufferedAmountAfter: this.ws.bufferedAmount,
    });
  }

  private drain() {
    for (;;) {
      const got = tryReadOnePdu(this.rx);
      if (!got) {
        break;
      }
      this.rx = this.rx.subarray(got.consumed);
      this.dispatch(got.pdu);
    }
  }

  private dispatch(pdu: ParsedPdu) {
    const proto = getProto();
    const fileCmd = proto.IM.BaseDefine.FileCmdID;
    recordFileWsDebug({ phase: 'dispatch', commandId: pdu.commandId, serviceId: pdu.serviceId, bytes: pdu.length });
    try {
      if (pdu.commandId === fileCmd.CID_FILE_LOGIN_RES) {
        const rsp = proto.IM.File.IMFileLoginRsp.deserializeBinary(pdu.body);
        recordFileWsDebug({ phase: 'loginRsp', resultCode: rsp.getResultCode() });
        if (rsp.getResultCode() === proto.IM.BaseDefine.FileServerError.FILE_SERVER_ERRNO_OK) {
          this.resolveLogin?.(rsp);
        } else {
          this.rejectLogin?.(new Error(`File login failed: ${rsp.getResultCode()}`));
        }
        this.cleanupLoginState();
        return;
      }

      if (pdu.commandId === fileCmd.CID_FILE_STATE) {
        const state = proto.IM.File.IMFileState.deserializeBinary(pdu.body);
        recordFileWsDebug({ phase: 'state', state: state.getState(), taskId: state.getTaskId?.() });
        this.handlers.onState?.(state);
        return;
      }

      if (pdu.commandId === fileCmd.CID_FILE_PULL_DATA_REQ) {
        const req = proto.IM.File.IMFilePullDataReq.deserializeBinary(pdu.body);
        recordFileWsDebug({
          phase: 'pullReq',
          taskId: req.getTaskId?.(),
          offset: req.getOffset?.(),
          dataSize: req.getDataSize?.(),
        });
        this.handlers.onPullDataReq?.(req);
        return;
      }

      if (pdu.commandId === fileCmd.CID_FILE_PULL_DATA_RSP) {
        const rsp = proto.IM.File.IMFilePullDataRsp.deserializeBinary(pdu.body);
        recordFileWsDebug({
          phase: 'pullRsp',
          taskId: rsp.getTaskId?.(),
          offset: rsp.getOffset?.(),
          bytes: rsp.getData_asU8?.().length ?? 0,
        });
        this.handlers.onPullDataRsp?.(rsp);
      }
    } catch (error) {
      recordFileWsDebug({
        phase: 'dispatchError',
        commandId: pdu.commandId,
        serviceId: pdu.serviceId,
        error: String(error),
      });
      throw error;
    }
  }
}
